import { useRef, useState } from 'react'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { Dialog, DialogContent, DialogTitle } from '@mui/material'
import { fineBoxIdFrom, initialsFrom } from './logic'

async function run(dispatch, onError, action) {
  const result = await dispatch(action)
  onError(result?.ok ? '' : (result?.error || 'Det kunne ikke gemmes'))
  return Boolean(result?.ok)
}

const fineDialogPaper = {
  className: 'hold-create-paper',
  sx: {
    backgroundColor: '#221c24',
    color: '#f3eadf',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: '18px',
    boxShadow: '0 24px 60px rgba(0, 0, 0, 0.55)',
  },
}

const fineDialogBackdrop = {
  sx: {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
}

export function CreateFineModal({ open, text, amount, busy, onText, onAmount, onClose, onSubmit }) {
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={fineDialogPaper}
      BackdropProps={fineDialogBackdrop}
    >
      <DialogTitle sx={{ color: '#f7f4ef', fontWeight: 800, paddingBottom: 0 }}>Opret bøde</DialogTitle>
      <DialogContent>
        <form className="hold-form hold-create-form" onSubmit={onSubmit}>
          <label>
            Bøde
            <input value={text} maxLength={80} onChange={(event) => onText(event.target.value)} placeholder="Hvad er bøden for?" />
          </label>
          <label>
            Pris
            <span className="hold-create-price">
              <input value={amount} type="number" step="1" onChange={(event) => onAmount(event.target.value)} placeholder="Hvor meget koster bøden?" />
              <span>kr</span>
            </span>
          </label>
          <p className="hold-meta"></p>
          <button type="submit" className="hold-action hold-action--save" disabled={busy || !text.trim() || !(Number.isInteger(Math.round(Number(amount))) && Math.round(Number(amount)) !== 0 && Math.abs(Math.round(Number(amount))) <= 10000)}>
            {busy ? 'Opretter…' : 'Opret'}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function EditFineModal({ open, text, amount, count = '1', busy, onText, onAmount, onCount, onClose, onSubmit }) {
  const price = Math.round(Number(amount))
  const copies = Math.round(Number(count))
  const ready = Boolean(String(text || '').trim())
    && Number.isInteger(price) && price !== 0 && Math.abs(price) <= 10000
    && Number.isInteger(copies) && copies >= 1 && copies <= 20
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={fineDialogPaper}
      BackdropProps={fineDialogBackdrop}
    >
      <DialogTitle sx={{ color: '#f7f4ef', fontWeight: 800, paddingBottom: 0 }}>Ret bøde</DialogTitle>
      <DialogContent>
        <form className="hold-form hold-create-form" onSubmit={onSubmit}>
          <label>
            Bøde
            <input value={text} maxLength={80} onChange={(event) => onText(event.target.value)} placeholder="Hvad er bøden for?" />
          </label>
          <label>
            Pris
            <span className="hold-create-price">
              <input value={amount} type="number" step="1" onChange={(event) => onAmount(event.target.value)} placeholder="Hvor meget koster bøden?" />
              <span>kr</span>
            </span>
          </label>
          <label>
            Antal
            <input value={count} type="number" min="1" max="20" onChange={(event) => onCount(event.target.value)} />
          </label>
          {ready ? <p className="hold-meta">I alt {price * copies} kr.</p> : <p className="hold-meta"></p>}
          <button type="submit" className="hold-action hold-action--save" disabled={busy || !ready}>
            {busy ? 'Gemmer…' : 'Gem'}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function GiveFineModal({ open, team, season, matchId = '', nameOf, dispatch, onError, onClose }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={fineDialogPaper}
      BackdropProps={fineDialogBackdrop}
    >
      <DialogTitle sx={{ color: '#f7f4ef', fontWeight: 800, paddingBottom: 0 }}>Giv bøde</DialogTitle>
      <DialogContent>
        {season.current ? (
          <GiveForm
            key={open ? `open-${matchId}` : 'closed'}
            bare
            steps
            matchId={matchId}
            team={team}
            season={season}
            nameOf={nameOf}
            dispatch={dispatch}
            onError={onError}
            onDone={onClose}
          />
        ) : (
          <p className="hold-copy">Nye bøder gives i den aktuelle sæson.</p>
        )}
      </DialogContent>
    </Dialog>
  )
}

export function FineBoxModal({ open, mode, busy, onClose, onConfirm }) {
  const joining = mode === 'join'
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={fineDialogPaper}
      BackdropProps={fineDialogBackdrop}
    >
      <DialogTitle sx={{ color: '#f7f4ef', fontWeight: 800, paddingBottom: 0 }}>
        {joining ? 'Vil du være med i bødekassen?' : 'Vil du ikke være med mere?'}
      </DialogTitle>
      <DialogContent>
        {joining ? null : <p className="hold-copy">Du får ikke nye bøder. Dem du allerede har, bliver stående.</p>}
        <div className="hold-modal-actions">
          <button type="button" className="hold-action hold-action--ghost" onClick={onClose} disabled={busy}>
            {joining ? 'Nej' : 'Annuller'}
          </button>
          <button
            type="button"
            className={`hold-action ${joining ? 'hold-action--save' : 'hold-action--leave'}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Gemmer…' : joining ? 'Ja' : 'Forlad'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function Confirm({ title, body, busy, onCancel, onConfirm, confirmLabel = 'Slet', busyLabel = 'Sletter…', danger = true }) {
  return (
    <Dialog
      open
      onClose={busy ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={fineDialogPaper}
      BackdropProps={fineDialogBackdrop}
    >
      <DialogTitle sx={{ color: '#f7f4ef', fontWeight: 800, paddingBottom: 0 }}>{title}</DialogTitle>
      <DialogContent>
        <p className="hold-copy">{body}</p>
        <div className={`hold-modal-actions${onConfirm ? '' : ' is-single'}`}>
          {onConfirm ? (
            <button type="button" className="hold-action hold-action--ghost" onClick={onCancel} disabled={busy}>Annuller</button>
          ) : null}
          {onConfirm ? (
            <button type="button" className={`hold-action ${danger ? 'hold-action--leave' : 'hold-action--save'}`} onClick={onConfirm} disabled={busy}>
              {busy ? busyLabel : confirmLabel}
            </button>
          ) : (
            <button type="button" className="hold-action" onClick={onCancel}>Luk</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function matchTitle(match) {
  return `${match.status === 'cancelled' ? 'Aflyst · ' : ''}${match.home ? 'Hjemme' : 'Ude'} mod ${match.opponent}`
}

function GiveTypes({ types, counts, onCount }) {
  return (
    <ul className="hold-give-list hold-give-types">
      {types.map((type) => {
        const count = counts[type.id] || 0
        return (
          <li key={type.id}>
            <div className={`hold-give-pick hold-give-type ${count ? 'is-on' : ''}`}>
              <button type="button" className="hold-give-type-name" onClick={() => onCount(type.id, count ? 0 : 1)}>
                <span>{type.text}</span>
                <span className="hold-give-price">{type.amount * (count || 1)} kr</span>
              </button>
              <span className="hold-give-slot">
                {count > 0 ? (
                  <span className="hold-give-count">
                    <button type="button" aria-label={`Færre ${type.text}`} onClick={() => onCount(type.id, count - 1)}>−</button>
                    <span>{count}</span>
                    <button type="button" aria-label={`Flere ${type.text}`} disabled={count >= 20} onClick={() => onCount(type.id, count + 1)}>+</button>
                  </span>
                ) : (
                  <button type="button" className="hold-give-add" aria-label={`Tilføj ${type.text}`} onClick={() => onCount(type.id, 1)}>+</button>
                )}
              </span>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

function GivePeople({ members, picked, nameOf, onToggle, onAll, signedUp = null, label = false }) {
  return (
    <>
      {label || members.length > 1 ? (
        <div className="hold-give-people-head">
          {label ? <p className="hold-meta">Personer</p> : <span />}
          {members.length > 1 ? (
            <button type="button" className="hold-text-button" onClick={onAll}>
              {picked.length === members.length ? 'Fravælg alle' : 'Vælg alle'}
            </button>
          ) : null}
        </div>
      ) : null}
      {members.length === 0 ? <p className="hold-empty">Ingen er med i bødekassen endnu.</p> : (
        <ul className="hold-give-list">
          {members.map((member) => {
            const on = picked.includes(member.id)
            const person = nameOf(member.id)
            return (
              <li key={member.id}>
                <button
                  type="button"
                  className={`hold-give-pick ${on ? 'is-on' : ''}`}
                  aria-pressed={on}
                  onClick={() => onToggle(member.id)}
                >
                  <span className="hold-fine-avatar hold-give-avatar" aria-hidden="true">{initialsFrom(person)}</span>
                  <span className="hold-give-name">
                    {person}
                    {signedUp && !signedUp.has(member.id) ? <em>Ikke tilmeldt</em> : null}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}

function customFineReady(text, amount) {
  const value = Math.round(Number(amount))
  return Boolean(String(text || '').trim()) && Number.isInteger(value) && value !== 0 && Math.abs(value) <= 10000
}

function CustomFine({ text, amount, count, onText, onAmount, onCount }) {
  return (
    <div className="hold-custom-fine">
      <p className="hold-meta">Ikke på listen</p>
      <label>
        Titel
        <input value={text} maxLength={80} onChange={(event) => onText(event.target.value)} placeholder="Hvad er bøden for?" />
      </label>
      <label>
        Pris
        <span className="hold-create-price">
          <input value={amount} type="number" step="1" onChange={(event) => onAmount(event.target.value)} placeholder="Hvor meget koster bøden?" />
          <span>kr</span>
        </span>
      </label>
      <label>
        Antal
        <input
          value={count}
          type="number"
          min="1"
          max="20"
          onChange={(event) => onCount(event.target.value)}
        />
      </label>
    </div>
  )
}

function GiveForm({ team, season, matchId = '', nameOf, dispatch, onError, onDone, compact = false, bare = false, steps = false }) {
  const [open, setOpen] = useState(!compact)
  const [step, setStep] = useState(0)
  const [counts, setCounts] = useState({})
  const [customText, setCustomText] = useState('')
  const [customAmount, setCustomAmount] = useState('')
  const [customCount, setCustomCount] = useState('1')
  const [chosenMatch, setChosenMatch] = useState(steps ? (matchId || null) : (matchId || ''))
  const giveAfter = useRef(0)
  const [picked, setPicked] = useState([])
  const [busy, setBusy] = useState(false)
  const inBox = team.members
    .filter((member) => member.inFineBox)
    .sort((a, b) => nameOf(a.id).localeCompare(nameOf(b.id), 'da'))
  const activeMatchId = chosenMatch === null ? matchId : chosenMatch
  const activeMatch = activeMatchId ? season.matches.find((item) => item.id === activeMatchId) || null : null
  const signedUp = new Set(activeMatch?.attendees || [])
  const chosen = team.fineTypes
    .map((type) => ({ ...type, count: counts[type.id] || 0 }))
    .filter((type) => type.count > 0)
  const customOk = customFineReady(customText, customAmount)
  const customCopies = customOk ? Math.max(1, Math.min(20, Math.round(Number(customCount)) || 1)) : 0
  const customUnit = customOk ? Math.round(Number(customAmount)) : 0
  const fineCount = chosen.reduce((sum, type) => sum + type.count, 0) + customCopies
  const total = (chosen.reduce((sum, type) => sum + type.amount * type.count, 0) + customUnit * customCopies) * picked.length

  if (!season.current) return null
  if (compact && !open) {
    return (
      <button type="button" className="hold-action hold-action--ghost" onClick={() => setOpen(true)}>
        Giv bøde
      </button>
    )
  }

  const toggle = (id, setIds) => {
    setIds((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]))
  }
  const matchChosen = !steps || chosenMatch !== null
  const ready = fineCount > 0 && picked.length > 0 && matchChosen && !busy
  const who = picked.length === 1 ? nameOf(picked[0]) : `${picked.length} personer`
  const setCount = (id, count) => {
    const next = Math.max(0, Math.min(20, count))
    setCounts((current) => {
      if (!next) {
        const rest = { ...current }
        delete rest[id]
        return rest
      }
      return { ...current, [id]: next }
    })
  }
  const giveLabel = busy
    ? 'Giver…'
    : fineCount <= 1
      ? `Giv ${total} kr til ${who}`
      : `Giv ${fineCount} bøder · ${total} kr til ${who}`
  const give = async (event) => {
    event.preventDefault()
    if (!ready) return
    setBusy(true)
    for (const type of chosen) {
      const ok = await run(dispatch, onError, {
        type: 'GIVE_FINES',
        teamId: team.id,
        seasonId: season.id,
        typeId: type.id,
        mateIds: picked,
        matchId: (chosenMatch === null ? matchId : chosenMatch) || null,
        count: type.count,
      })
      if (!ok) {
        setBusy(false)
        return
      }
      setCounts((current) => {
        const rest = { ...current }
        delete rest[type.id]
        return rest
      })
    }
    if (customOk) {
      const ok = await run(dispatch, onError, {
        type: 'GIVE_FINES',
        teamId: team.id,
        seasonId: season.id,
        text: customText.trim(),
        amount: customUnit,
        mateIds: picked,
        matchId: (chosenMatch === null ? matchId : chosenMatch) || null,
        count: customCopies,
      })
      if (!ok) {
        setBusy(false)
        return
      }
      setCustomText('')
      setCustomAmount('')
      setCustomCount('1')
    }
    setBusy(false)
    setPicked([])
    setStep(0)
    if (onDone) onDone()
    else if (compact) setOpen(false)
  }
  const pickAll = () => setPicked(picked.length === inBox.length ? [] : inBox.map((member) => member.id))

  if (steps) {
    const route = ['Personer', 'Bøder', 'Kamp']
    const pickedMatch = season.matches.find((item) => item.id === chosenMatch) || null
    const unsigned = pickedMatch ? picked.filter((id) => !(pickedMatch.attendees || []).includes(id)) : []
    const stepCopy = step === 0
      ? 'Vælg en eller flere personer.'
      : step === 1
        ? `Vælg de bøder ${picked.length === 1 ? nameOf(picked[0]) : 'personerne'} skal have.`
        : 'Vælg en kamp, eller ingen kamp.'
    const canNext = step === 0 ? picked.length > 0 : fineCount > 0
    return (
      <form className="hold-form hold-create-form hold-give-route-form" onSubmit={(event) => event.preventDefault()}>
        <ol className="hold-give-route">
          {route.map((label, index) => (
            <li key={label} className={index < step ? 'is-done' : ''}>
              <button
                type="button"
                className={index === step ? 'is-on' : index < step ? 'is-done' : ''}
                disabled={index > step || busy}
                onClick={() => setStep(index)}
              >
                <span>{index + 1}</span>
                {label}
              </button>
            </li>
          ))}
        </ol>
        <div className="hold-give-route-body">
          <p className="hold-copy">{stepCopy}</p>
          {step === 0 ? (
            <GivePeople
              members={inBox}
              picked={picked}
              nameOf={nameOf}
              onToggle={(id) => toggle(id, setPicked)}
              onAll={pickAll}
              signedUp={activeMatch ? signedUp : null}
            />
          ) : null}
          {step === 1 ? (
            <>
              {team.fineTypes.length > 0 ? <GiveTypes types={team.fineTypes} counts={counts} onCount={setCount} /> : null}
              <CustomFine
                text={customText}
                amount={customAmount}
                count={customCount}
                onText={setCustomText}
                onAmount={setCustomAmount}
                onCount={setCustomCount}
              />
            </>
          ) : null}
          {step === 2 ? (
            <>
              <ul className="hold-give-list">
                <li>
                  <button
                    type="button"
                    className={`hold-give-pick ${chosenMatch === '' ? 'is-on' : ''}`}
                    aria-pressed={chosenMatch === ''}
                    onClick={() => setChosenMatch('')}
                  >
                    <span>Ingen kamp</span>
                  </button>
                </li>
                {season.matches.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={`hold-give-pick ${chosenMatch === item.id ? 'is-on' : ''}`}
                      aria-pressed={chosenMatch === item.id}
                      onClick={() => setChosenMatch(item.id)}
                    >
                      <span>{matchTitle(item)}</span>
                    </button>
                  </li>
                ))}
              </ul>
              {unsigned.length > 0 ? (
                <p className="hold-meta">
                  {unsigned.length === 1 ? `${nameOf(unsigned[0])} er ikke tilmeldt.` : `${unsigned.length} er ikke tilmeldt.`}
                </p>
              ) : null}
            </>
          ) : null}
        </div>
        <div className="hold-give-nav">
          {step > 0 ? (
            <button type="button" className="hold-action hold-action--ghost" disabled={busy} onClick={() => setStep(step - 1)}>
              Tilbage
            </button>
          ) : null}
          {step < 2 ? (
            <button
              type="button"
              className="hold-action hold-action--save"
              disabled={!canNext}
              onClick={() => {
                if (step === 1) giveAfter.current = Date.now() + 400
                setStep(step + 1)
              }}
            >
              Næste
            </button>
          ) : (
            <button
              type="button"
              className="hold-action hold-action--save"
              disabled={!ready}
              onClick={(event) => {
                if (Date.now() < giveAfter.current) return
                give(event)
              }}
            >
              {busy ? 'Giver…' : fineCount > 1 ? `Giv ${fineCount} bøder` : 'Giv bøde'}
            </button>
          )}
        </div>
      </form>
    )
  }

  return (
    <form className={`hold-form ${bare ? 'hold-create-form' : ''}`} onSubmit={give}>
      <p className="hold-meta">Bøder</p>
      {team.fineTypes.length > 0 ? <GiveTypes types={team.fineTypes} counts={counts} onCount={setCount} /> : null}
      <CustomFine
        text={customText}
        amount={customAmount}
        count={customCount}
        onText={setCustomText}
        onAmount={setCustomAmount}
        onCount={setCustomCount}
      />
      <GivePeople
        label
        members={inBox}
        picked={picked}
        nameOf={nameOf}
        onToggle={(id) => toggle(id, setPicked)}
        onAll={pickAll}
        signedUp={activeMatch ? signedUp : null}
      />
      {matchId ? null : (
        <label>
          Kamp
          <select value={chosenMatch} onChange={(event) => setChosenMatch(event.target.value)}>
            <option value="">Uden kamp</option>
            {season.matches.map((item) => (
              <option key={item.id} value={item.id}>{matchTitle(item)}</option>
            ))}
          </select>
        </label>
      )}
      <button type="submit" className="hold-action" disabled={!ready}>
        {busy ? 'Giver…' : chosen.length === 0 ? 'Vælg bøder' : picked.length === 0 ? 'Vælg personer' : giveLabel}
      </button>
    </form>
  )
}

function paidNow(season, mateId) {
  const fines = (season.fines || []).filter((fine) => fine.mateId === mateId)
  const given = fines.reduce((sum, fine) => sum + fine.amount, 0)
  const recorded = (season.finePayments || []).find((item) => item.mateId === mateId)
  const paidRaw = recorded
    ? recorded.amount
    : fines.reduce((sum, fine) => sum + (fine.paid ? fine.amount : 0), 0)
  const paid = Math.max(0, paidRaw)
  return { given, paid, owed: given - paid }
}

function PayForm({ team, season, nameOf, dispatch, onError, onDone }) {
  const people = [...new Set((season.fines || []).map((fine) => fine.mateId))]
    .sort((a, b) => nameOf(a).localeCompare(nameOf(b), 'da'))
  const [step, setStep] = useState(0)
  const [mateId, setMateId] = useState('')
  const [amount, setAmount] = useState('')
  const [keepPaid, setKeepPaid] = useState(true)
  const [confirmOver, setConfirmOver] = useState(false)
  const [busy, setBusy] = useState(false)
  const saveAfter = useRef(0)
  const current = mateId ? paidNow(season, mateId) : null
  const typed = amount.trim() === '' ? 0 : Number(amount)
  const typedOk = Number.isInteger(typed) && typed >= 0
  const kept = current && keepPaid ? current.paid : 0
  const finesTotal = current ? Math.max(0, current.given) : 0
  const total = typedOk ? kept + typed : null
  const over = total !== null && total > finesTotal
  const ready = Boolean(
    current
    && total !== null
    && total !== current.paid
    && !busy
  )
  const left = current && total !== null ? current.given - total : null

  if (people.length === 0) return <p className="hold-note">Ingen har bøder i sæsonen.</p>

  const route = ['Person', 'Beløb']
  const stepCopy = step === 0
    ? 'Vælg personen.'
    : current?.paid > 0 && keepPaid
      ? 'Det, der allerede er betalt, lægges sammen med beløbet.'
      : 'Skriv hvor meget der er betalt.'
  const save = async () => {
    if (!ready || Date.now() < saveAfter.current) return
    setBusy(true)
    const ok = await run(dispatch, onError, {
      type: 'SET_FINE_PAYMENT',
      teamId: team.id,
      seasonId: season.id,
      mateId,
      amount: total,
      entry: typed > 0 ? typed : null,
    })
    setBusy(false)
    if (!ok) return
    setConfirmOver(false)
    onDone()
  }
  const askSave = (event) => {
    event.preventDefault()
    if (!ready || Date.now() < saveAfter.current) return
    if (over) {
      setConfirmOver(true)
      return
    }
    save()
  }

  return (
    <>
    <form className="hold-form hold-create-form hold-give-route-form" onSubmit={(event) => event.preventDefault()}>
      <ol className="hold-give-route hold-give-route--two">
        {route.map((label, index) => (
          <li key={label} className={index < step ? 'is-done' : ''}>
            <button
              type="button"
              className={index === step ? 'is-on' : index < step ? 'is-done' : ''}
              disabled={index > step || busy}
              onClick={() => setStep(index)}
            >
              <span>{index + 1}</span>
              {label}
            </button>
          </li>
        ))}
      </ol>
      <div className="hold-give-route-body">
        <p className="hold-copy">{stepCopy}</p>
        {step === 0 ? (
          <ul className="hold-give-list">
            {people.map((id) => {
              const on = mateId === id
              const row = paidNow(season, id)
              return (
                <li key={id}>
                  <button
                    type="button"
                    className={`hold-give-pick ${on ? 'is-on' : ''}`}
                    aria-pressed={on}
                    onClick={() => {
                      if (on) {
                        setMateId('')
                        return
                      }
                      setMateId(id)
                      setAmount('')
                      setKeepPaid(true)
                    }}
                  >
                    <span className="hold-fine-avatar hold-give-avatar" aria-hidden="true">{initialsFrom(nameOf(id))}</span>
                    <span className="hold-give-name">{nameOf(id)}</span>
                    <span className="hold-give-price">{row.owed < 0 ? `Til gode ${-row.owed} kr` : `Skylder ${row.owed} kr`}</span>
                  </button>
                </li>
              )
            })}
          </ul>
        ) : null}
        {step === 1 && current ? (
          <>
            {current.paid > 0 && keepPaid ? (
              <div className="hold-pay-kept">
                <span>Betalt</span>
                <strong>{current.paid} kr</strong>
                <button type="button" className="hold-icon-button hold-icon-button--delete" aria-label="Fjern betalt beløb" onClick={() => setKeepPaid(false)}>
                  <DeleteOutlineIcon />
                </button>
              </div>
            ) : null}
            {current.paid > 0 && !keepPaid ? (
              <p className="hold-pay-restore">
                <span>{current.paid} kr er fjernet.</span>
                <button type="button" className="hold-text-button" onClick={() => setKeepPaid(true)}>Læg tilbage</button>
              </p>
            ) : null}
            <label>
              Beløb
              <span className="hold-create-price">
                <input
                  value={amount}
                  type="number"
                  min="0"
                  inputMode="numeric"
                  placeholder="0"
                  onChange={(event) => setAmount(event.target.value)}
                />
                <span>kr</span>
              </span>
            </label>
            <p className="hold-meta">
              I alt {total === null ? kept : total} kr.
              {left === null ? '' : left < 0 ? ` Til gode ${-left} kr.` : ` Skylder ${left} kr.`}
            </p>
            {over ? <p className="hold-pay-warn">Beløbet er højere end bøderne på {finesTotal} kr.</p> : null}
          </>
        ) : null}
      </div>
      <div className="hold-give-nav">
        {step > 0 ? (
          <button type="button" className="hold-action hold-action--ghost" disabled={busy} onClick={() => setStep(step - 1)}>
            Tilbage
          </button>
        ) : null}
        {step < 1 ? (
          <button
            type="button"
            className="hold-action hold-action--save"
            disabled={!mateId}
            onClick={() => {
              saveAfter.current = Date.now() + 400
              setStep(1)
            }}
          >
            Næste
          </button>
        ) : (
          <button type="button" className="hold-action hold-action--save" disabled={!ready} onClick={askSave}>
            {busy ? 'Gemmer…' : total === 0 ? 'Fjern betaling' : total > 0 ? `Registrer ${total} kr` : 'Registrer'}
          </button>
        )}
      </div>
    </form>
    {confirmOver ? (
      <Confirm
        title="Beløbet er højere end bøderne"
        body={`Der registreres ${total} kr. Bøderne er på ${finesTotal} kr.`}
        busy={busy}
        danger={false}
        confirmLabel="Gem"
        busyLabel="Gemmer…"
        onCancel={() => {
          if (!busy) setConfirmOver(false)
        }}
        onConfirm={save}
      />
    ) : null}
    </>
  )
}

function FineBoxForm({ team, dispatch, onError, onDone }) {
  const saved = team.fineBoxId
    ? `https://qr.mobilepay.dk/box/${team.fineBoxId}/pay-in`
    : ''
  const [link, setLink] = useState(saved)
  const [busy, setBusy] = useState(false)

  return (
    <form
      className="hold-form hold-create-form"
      onSubmit={async (event) => {
        event.preventDefault()
        if (busy) return
        const id = fineBoxIdFrom(link)
        if (id === null) {
          onError('Indsæt linket fra MobilePay Box')
          return
        }
        if (id === (team.fineBoxId || '')) {
          onDone()
          return
        }
        setBusy(true)
        const ok = await run(dispatch, onError, { type: 'SET_FINE_BOX', teamId: team.id, link: id })
        setBusy(false)
        if (ok) onDone()
      }}
    >
      <p className="hold-meta">Indsæt linket fra boxen i MobilePay. Så kan den, der skylder, betale fra Mine bøder.</p>
      <label>
        Link
        <input
          value={link}
          inputMode="url"
          placeholder="https://qr.mobilepay.dk/box/…/pay-in"
          onChange={(event) => setLink(event.target.value)}
        />
      </label>
      <button type="submit" className="hold-action hold-action--save" disabled={busy || link.trim() === saved}>
        {busy ? 'Gemmer…' : 'Gem'}
      </button>
    </form>
  )
}

export function FineBoxLinkModal({ open, team, dispatch, onError, onClose }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={fineDialogPaper}
      BackdropProps={fineDialogBackdrop}
    >
      <DialogTitle sx={{ color: '#f7f4ef', fontWeight: 800, paddingBottom: 0 }}>MobilePay Box</DialogTitle>
      <DialogContent>
        {open ? (
          <FineBoxForm
            key={team.fineBoxId || 'empty'}
            team={team}
            dispatch={dispatch}
            onError={onError}
            onDone={onClose}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

export function PayModal({ open, team, season, nameOf, dispatch, onError, onClose }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={fineDialogPaper}
      BackdropProps={fineDialogBackdrop}
    >
      <DialogTitle sx={{ color: '#f7f4ef', fontWeight: 800, paddingBottom: 0 }}>Indbetaling</DialogTitle>
      <DialogContent>
        {open ? (
          <PayForm
            key={season.id}
            team={team}
            season={season}
            nameOf={nameOf}
            dispatch={dispatch}
            onError={onError}
            onDone={onClose}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
